import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

export default class extends Controller {
  static targets = ["picker", "startTime", "endTime", "closedMessage", "hint"]
  static values = {
    closedDates: Array,
    operationHours: Object,
    surcharges: Array,
    defaultDate: String
  }

  connect() {
    const disabledRanges = (this.closedDatesValue || []).map(r => ({ from: r.from, to: r.to }))
    const ops = this.operationHoursValue || {}

    this.fp = flatpickr(this.pickerTarget, {
      enableTime: true,
      minuteIncrement: 30,
      dateFormat: "Y-m-d H:i",
      altInput: true,
      altFormat: "D, M j Y — h:i K",
      time_24hr: false,
      minDate: "today",
      defaultDate: this.defaultDateValue ? `${this.defaultDateValue} 10:00` : null,
      disable: [
        ...disabledRanges,
        (date) => {
          const cfg = ops[date.getDay()]
          return !cfg || cfg.closed
        }
      ],
      onReady: (dates, _str, instance) => {
        this.fp = instance
        this.applyHoursFor(dates[0])
        this.syncHidden(dates[0])
      },
      onChange: (dates, _str, instance) => {
        this.fp = instance
        this.applyHoursFor(dates[0])
        this.syncHidden(dates[0])
      }
    })
  }

  disconnect() {
    if (this.fp) this.fp.destroy()
  }

  applyHoursFor(date) {
    if (!date) return
    const cfg = (this.operationHoursValue || {})[date.getDay()]
    if (!cfg || cfg.closed) {
      this.fp.set("minTime", "00:00")
      this.fp.set("maxTime", "00:00")
      this.closedMessageTarget.classList.remove("d-none")
      this.hintTarget.textContent = ""
      return
    }
    this.closedMessageTarget.classList.add("d-none")
    const minTime = cfg.start
    const maxTime = cfg.end === "24:00" ? "23:30" : cfg.end
    this.fp.set("minTime", minTime)
    this.fp.set("maxTime", maxTime)
    this.renderHint(cfg)
  }

  // Given an end time like "22:00" or "24:00", return the last selectable
  // 30-min start time so a booking doesn't start at closing time.
  lastSlotBefore(endStr) {
    let [h, m] = endStr.split(":").map(n => parseInt(n, 10))
    let total = h * 60 + m - 30
    if (total < 0) total = 0
    const hh = Math.floor(total / 60) % 24
    const mm = total % 60
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`
  }

  renderHint(cfg) {
    this.hintTarget.textContent = `Operating hours today: ${this.fmt12(cfg.start)} – ${this.fmt12(cfg.end)}.`
  }

  fmt12(hhmm) {
    const [h, m] = hhmm.split(":").map(n => parseInt(n, 10))
    if (h === 24) return "12:00 AM"
    const period = h >= 12 ? "PM" : "AM"
    const hr12 = ((h + 11) % 12) + 1
    return `${hr12}:${String(m).padStart(2, "0")} ${period}`
  }

  syncHidden(date) {
    if (!date) {
      this.startTimeTarget.value = ""
      return
    }
    const y = date.getFullYear()
    const mo = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    const hh = String(date.getHours()).padStart(2, "0")
    const mm = String(date.getMinutes()).padStart(2, "0")
    this.startTimeTarget.value = `${y}-${mo}-${d}T${hh}:${mm}:00`
    this.startTimeTarget.dispatchEvent(new Event("change"))
  }
}
